import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Check, Lock, Unlock, ChevronDown } from 'lucide-react';

// Digital/Social aspect ratios
const DIGITAL_RATIOS = [
    { label: 'Free', value: null },
    { label: '1:1', value: 1, description: 'Square' },
    { label: '4:5', value: 4 / 5, description: 'Instagram Portrait' },
    { label: '5:4', value: 5 / 4, description: 'Instagram Landscape' },
    { label: '9:16', value: 9 / 16, description: 'Stories/Reels' },
    { label: '16:9', value: 16 / 9, description: 'Widescreen' },
    { label: '4:3', value: 4 / 3, description: 'Standard' },
    { label: '3:2', value: 3 / 2, description: 'Classic Photo' },
    { label: '2:3', value: 2 / 3, description: 'Portrait Photo' },
];

// Print sizes (ISO and common formats) - using portrait orientation (width/height)
const PRINT_RATIOS = [
    { label: 'A4', value: 210 / 297, description: '210×297mm' },
    { label: 'A5', value: 148 / 210, description: '148×210mm' },
    { label: 'A3', value: 297 / 420, description: '297×420mm' },
    { label: 'A6', value: 105 / 148, description: '105×148mm' },
    { label: 'B5', value: 176 / 250, description: '176×250mm' },
    { label: 'B4', value: 250 / 353, description: '250×353mm' },
    { label: 'Letter', value: 8.5 / 11, description: '8.5×11"' },
    { label: 'Legal', value: 8.5 / 14, description: '8.5×14"' },
    { label: 'Tabloid', value: 11 / 17, description: '11×17"' },
    { label: '4×6"', value: 4 / 6, description: 'Photo Print' },
    { label: '5×7"', value: 5 / 7, description: 'Photo Print' },
    { label: '8×10"', value: 8 / 10, description: 'Photo Print' },
];

const RATIO_CATEGORIES = [
    { label: 'Digital', ratios: DIGITAL_RATIOS },
    { label: 'Print', ratios: PRINT_RATIOS },
];

const HANDLE_SIZE = 10;
const MIN_CROP_SIZE = 30;

const ImageCropCanvas = ({ imageUrl, onCropComplete, onCancel }) => {
    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const containerRef = useRef(null);

    const [imageLoaded, setImageLoaded] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(null);
    const [selectedRatioLabel, setSelectedRatioLabel] = useState('Free');
    const [displayScale, setDisplayScale] = useState(1);
    const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
    const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

    // Crop rectangle in display coordinates
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });

    // Drag state
    const [dragType, setDragType] = useState(null); // 'move', 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
    const [dragStart, setDragStart] = useState(null);
    const [cropStart, setCropStart] = useState(null);

    // Dropdown and custom ratio state
    const [showRatioDropdown, setShowRatioDropdown] = useState(false);
    const [customWidth, setCustomWidth] = useState('');
    const [customHeight, setCustomHeight] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowRatioDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle selecting a preset ratio
    const handleSelectRatio = (ratio) => {
        setAspectRatio(ratio.value);
        setSelectedRatioLabel(ratio.label);
        setShowRatioDropdown(false);
        setCustomWidth('');
        setCustomHeight('');
    };

    // Handle applying custom ratio
    const handleApplyCustomRatio = () => {
        const w = parseFloat(customWidth);
        const h = parseFloat(customHeight);
        if (w > 0 && h > 0) {
            const ratio = w / h;
            setAspectRatio(ratio);
            setSelectedRatioLabel(`${w}:${h}`);
            setShowRatioDropdown(false);
        }
    };

    // Load the image
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            imageRef.current = img;
            setImageLoaded(true);
        };
        img.onerror = () => {
            console.error('Failed to load image for cropping');
        };
        img.src = imageUrl;
    }, [imageUrl]);

    // Calculate display dimensions and initialize crop
    useEffect(() => {
        if (!imageLoaded || !imageRef.current || !containerRef.current) return;

        const img = imageRef.current;
        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        const maxW = containerRect.width - 80;
        const maxH = containerRect.height - 80;

        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const dw = img.width * scale;
        const dh = img.height * scale;
        const ox = (containerRect.width - dw) / 2;
        const oy = (containerRect.height - dh) / 2;

        setDisplayScale(scale);
        setDisplaySize({ width: dw, height: dh });
        setImageOffset({ x: ox, y: oy });

        // Initialize crop to full image
        const margin = Math.min(dw, dh) * 0.1;
        setCrop({
            x: margin,
            y: margin,
            width: dw - margin * 2,
            height: dh - margin * 2
        });
    }, [imageLoaded]);

    // Apply aspect ratio constraint when ratio changes
    useEffect(() => {
        if (!aspectRatio || !displaySize.width) return;

        setCrop(prev => {
            const centerX = prev.x + prev.width / 2;
            const centerY = prev.y + prev.height / 2;

            let newW = prev.width;
            let newH = newW / aspectRatio;

            if (newH > displaySize.height * 0.8) {
                newH = displaySize.height * 0.8;
                newW = newH * aspectRatio;
            }

            let newX = centerX - newW / 2;
            let newY = centerY - newH / 2;

            // Clamp
            newX = Math.max(0, Math.min(newX, displaySize.width - newW));
            newY = Math.max(0, Math.min(newY, displaySize.height - newH));

            return { x: newX, y: newY, width: newW, height: newH };
        });
    }, [aspectRatio, displaySize]);

    const clampCrop = useCallback((c) => {
        let { x, y, width, height } = c;
        width = Math.max(MIN_CROP_SIZE, Math.min(width, displaySize.width));
        height = Math.max(MIN_CROP_SIZE, Math.min(height, displaySize.height));
        x = Math.max(0, Math.min(x, displaySize.width - width));
        y = Math.max(0, Math.min(y, displaySize.height - height));
        return { x, y, width, height };
    }, [displaySize]);

    const getHandleAtPoint = useCallback((px, py) => {
        const { x, y, width, height } = crop;
        const hs = HANDLE_SIZE;

        // Corners
        if (Math.abs(px - x) <= hs && Math.abs(py - y) <= hs) return 'nw';
        if (Math.abs(px - (x + width)) <= hs && Math.abs(py - y) <= hs) return 'ne';
        if (Math.abs(px - x) <= hs && Math.abs(py - (y + height)) <= hs) return 'sw';
        if (Math.abs(px - (x + width)) <= hs && Math.abs(py - (y + height)) <= hs) return 'se';

        // Edges
        if (Math.abs(py - y) <= hs && px > x + hs && px < x + width - hs) return 'n';
        if (Math.abs(py - (y + height)) <= hs && px > x + hs && px < x + width - hs) return 's';
        if (Math.abs(px - x) <= hs && py > y + hs && py < y + height - hs) return 'w';
        if (Math.abs(px - (x + width)) <= hs && py > y + hs && py < y + height - hs) return 'e';

        // Inside
        if (px >= x && px <= x + width && py >= y && py <= y + height) return 'move';

        return null;
    }, [crop]);

    const getCursorForHandle = (handle) => {
        const cursors = {
            nw: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', se: 'nwse-resize',
            n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
            move: 'move'
        };
        return cursors[handle] || 'crosshair';
    };

    const handleMouseDown = useCallback((e) => {
        const rect = containerRef.current.getBoundingClientRect();
        const px = e.clientX - rect.left - imageOffset.x;
        const py = e.clientY - rect.top - imageOffset.y;

        const handle = getHandleAtPoint(px, py);
        if (!handle) return;

        setDragType(handle);
        setDragStart({ x: e.clientX, y: e.clientY });
        setCropStart({ ...crop });
        e.preventDefault();
    }, [crop, imageOffset, getHandleAtPoint]);

    const handleMouseMove = useCallback((e) => {
        if (!dragType || !dragStart || !cropStart) {
            // Update cursor
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const px = e.clientX - rect.left - imageOffset.x;
                const py = e.clientY - rect.top - imageOffset.y;
                const handle = getHandleAtPoint(px, py);
                containerRef.current.style.cursor = handle ? getCursorForHandle(handle) : 'default';
            }
            return;
        }

        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;

        let newCrop = { ...cropStart };

        if (dragType === 'move') {
            newCrop.x = cropStart.x + dx;
            newCrop.y = cropStart.y + dy;
        } else {
            // Resize handles
            if (dragType.includes('w')) {
                newCrop.x = cropStart.x + dx;
                newCrop.width = cropStart.width - dx;
            }
            if (dragType.includes('e')) {
                newCrop.width = cropStart.width + dx;
            }
            if (dragType.includes('n')) {
                newCrop.y = cropStart.y + dy;
                newCrop.height = cropStart.height - dy;
            }
            if (dragType.includes('s')) {
                newCrop.height = cropStart.height + dy;
            }

            // Enforce min size
            if (newCrop.width < MIN_CROP_SIZE) {
                if (dragType.includes('w')) {
                    newCrop.x = cropStart.x + cropStart.width - MIN_CROP_SIZE;
                }
                newCrop.width = MIN_CROP_SIZE;
            }
            if (newCrop.height < MIN_CROP_SIZE) {
                if (dragType.includes('n')) {
                    newCrop.y = cropStart.y + cropStart.height - MIN_CROP_SIZE;
                }
                newCrop.height = MIN_CROP_SIZE;
            }

            // Enforce aspect ratio
            if (aspectRatio) {
                if (dragType === 'n' || dragType === 's') {
                    newCrop.width = newCrop.height * aspectRatio;
                    if (dragType === 'n') {
                        newCrop.x = cropStart.x + (cropStart.width - newCrop.width) / 2;
                    }
                } else if (dragType === 'e' || dragType === 'w') {
                    newCrop.height = newCrop.width / aspectRatio;
                    if (dragType === 'w') {
                        newCrop.y = cropStart.y + (cropStart.height - newCrop.height) / 2;
                    }
                } else {
                    // Corner resize - adjust height based on width
                    newCrop.height = newCrop.width / aspectRatio;
                    if (dragType.includes('n')) {
                        newCrop.y = cropStart.y + cropStart.height - newCrop.height;
                    }
                }
            }
        }

        setCrop(clampCrop(newCrop));
        e.preventDefault();
    }, [dragType, dragStart, cropStart, aspectRatio, clampCrop, imageOffset, getHandleAtPoint]);

    const handleMouseUp = useCallback(() => {
        setDragType(null);
        setDragStart(null);
        setCropStart(null);
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    // Draw canvas
    useEffect(() => {
        if (!imageLoaded || !canvasRef.current || !imageRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;

        // Clear
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw image
        ctx.drawImage(imageRef.current, imageOffset.x, imageOffset.y, displaySize.width, displaySize.height);

        // Draw darkened overlay outside crop
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';

        const cx = imageOffset.x + crop.x;
        const cy = imageOffset.y + crop.y;
        const cw = crop.width;
        const ch = crop.height;

        // Top
        ctx.fillRect(0, 0, canvas.width, cy);
        // Bottom
        ctx.fillRect(0, cy + ch, canvas.width, canvas.height - cy - ch);
        // Left
        ctx.fillRect(0, cy, cx, ch);
        // Right
        ctx.fillRect(cx + cw, cy, canvas.width - cx - cw, ch);

        // Draw crop border
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.strokeRect(cx, cy, cw, ch);

        // Draw rule-of-thirds grid
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 1;
        for (let i = 1; i <= 2; i++) {
            ctx.beginPath();
            ctx.moveTo(cx + (cw * i) / 3, cy);
            ctx.lineTo(cx + (cw * i) / 3, cy + ch);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx, cy + (ch * i) / 3);
            ctx.lineTo(cx + cw, cy + (ch * i) / 3);
            ctx.stroke();
        }

        // Draw corner handles
        ctx.fillStyle = 'white';
        const hs = HANDLE_SIZE;
        const corners = [
            [cx, cy], [cx + cw, cy],
            [cx, cy + ch], [cx + cw, cy + ch]
        ];
        corners.forEach(([hx, hy]) => {
            ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
        });

        // Draw edge handles
        const edges = [
            [cx + cw / 2, cy], [cx + cw / 2, cy + ch],
            [cx, cy + ch / 2], [cx + cw, cy + ch / 2]
        ];
        edges.forEach(([hx, hy]) => {
            ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
        });

    }, [imageLoaded, crop, imageOffset, displaySize]);

    const handleApply = () => {
        if (!imageRef.current) return;

        const img = imageRef.current;

        // Convert display crop to actual image pixels
        const srcX = Math.round(crop.x / displayScale);
        const srcY = Math.round(crop.y / displayScale);
        const srcW = Math.round(crop.width / displayScale);
        const srcH = Math.round(crop.height / displayScale);

        // Create offscreen canvas for the cropped result
        const offscreen = document.createElement('canvas');
        offscreen.width = srcW;
        offscreen.height = srcH;
        const ctx = offscreen.getContext('2d');
        ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

        const dataUrl = offscreen.toDataURL('image/png');
        onCropComplete(dataUrl);
    };

    // Pixel dimensions of the crop in original image space
    const cropPixelW = Math.round(crop.width / displayScale);
    const cropPixelH = Math.round(crop.height / displayScale);

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Top toolbar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(0,0,0,0.8)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <span style={{ color: 'white', fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                        Crop Image
                    </span>

                    {/* Aspect ratio dropdown */}
                    <div ref={dropdownRef} style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowRatioDropdown(!showRatioDropdown)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 12px',
                                borderRadius: 'var(--radius-sm)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                background: aspectRatio ? 'var(--color-primary)' : 'transparent',
                                color: 'white',
                                fontSize: 'var(--font-size-sm)',
                                cursor: 'pointer',
                                minWidth: '120px',
                                justifyContent: 'space-between'
                            }}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {aspectRatio ? <Lock size={12} /> : <Unlock size={12} />}
                                {selectedRatioLabel}
                            </span>
                            <ChevronDown size={14} style={{
                                transform: showRatioDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                            }} />
                        </button>

                        {showRatioDropdown && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '4px',
                                background: 'var(--color-background, #1a1a1a)',
                                border: '1px solid rgba(255,255,255,0.15)',
                                borderRadius: 'var(--radius-md)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                                zIndex: 1000,
                                minWidth: '280px',
                                maxHeight: '400px',
                                overflowY: 'auto'
                            }}>
                                {/* Custom ratio input */}
                                <div style={{
                                    padding: '12px',
                                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                                }}>
                                    <div style={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: 'var(--font-size-xs)',
                                        marginBottom: '8px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Custom Ratio
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <input
                                            type="number"
                                            placeholder="W"
                                            value={customWidth}
                                            onChange={(e) => setCustomWidth(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCustomRatio()}
                                            style={{
                                                width: '60px',
                                                padding: '6px 8px',
                                                borderRadius: 'var(--radius-sm)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                background: 'rgba(255,255,255,0.05)',
                                                color: 'white',
                                                fontSize: 'var(--font-size-sm)',
                                                textAlign: 'center'
                                            }}
                                        />
                                        <span style={{ color: 'rgba(255,255,255,0.5)' }}>:</span>
                                        <input
                                            type="number"
                                            placeholder="H"
                                            value={customHeight}
                                            onChange={(e) => setCustomHeight(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleApplyCustomRatio()}
                                            style={{
                                                width: '60px',
                                                padding: '6px 8px',
                                                borderRadius: 'var(--radius-sm)',
                                                border: '1px solid rgba(255,255,255,0.2)',
                                                background: 'rgba(255,255,255,0.05)',
                                                color: 'white',
                                                fontSize: 'var(--font-size-sm)',
                                                textAlign: 'center'
                                            }}
                                        />
                                        <button
                                            onClick={handleApplyCustomRatio}
                                            disabled={!customWidth || !customHeight}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: 'var(--radius-sm)',
                                                border: 'none',
                                                background: (customWidth && customHeight) ? 'var(--color-primary)' : 'rgba(255,255,255,0.1)',
                                                color: 'white',
                                                fontSize: 'var(--font-size-xs)',
                                                cursor: (customWidth && customHeight) ? 'pointer' : 'not-allowed',
                                                opacity: (customWidth && customHeight) ? 1 : 0.5
                                            }}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>

                                {/* Ratio categories */}
                                {RATIO_CATEGORIES.map((category) => (
                                    <div key={category.label}>
                                        <div style={{
                                            padding: '8px 12px',
                                            color: 'rgba(255,255,255,0.5)',
                                            fontSize: 'var(--font-size-xs)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                                        }}>
                                            {category.label}
                                        </div>
                                        {category.ratios.map((ratio) => (
                                            <button
                                                key={ratio.label}
                                                onClick={() => handleSelectRatio(ratio)}
                                                style={{
                                                    width: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '10px 12px',
                                                    border: 'none',
                                                    background: selectedRatioLabel === ratio.label ? 'rgba(255,255,255,0.1)' : 'transparent',
                                                    color: 'white',
                                                    cursor: 'pointer',
                                                    textAlign: 'left',
                                                    fontSize: 'var(--font-size-sm)'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = selectedRatioLabel === ratio.label ? 'rgba(255,255,255,0.1)' : 'transparent'}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {ratio.value ? <Lock size={12} style={{ opacity: 0.5 }} /> : <Unlock size={12} style={{ opacity: 0.5 }} />}
                                                    {ratio.label}
                                                </span>
                                                {ratio.description && (
                                                    <span style={{
                                                        color: 'rgba(255,255,255,0.4)',
                                                        fontSize: 'var(--font-size-xs)'
                                                    }}>
                                                        {ratio.description}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    {/* Dimensions display */}
                    {imageLoaded && (
                        <span style={{
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'monospace'
                        }}>
                            {cropPixelW} x {cropPixelH} px
                        </span>
                    )}

                    <button
                        onClick={onCancel}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: 'var(--radius-md)',
                            color: 'white',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-sm)'
                        }}
                    >
                        <X size={16} />
                        Cancel
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={!imageLoaded}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '8px 16px',
                            background: 'var(--color-primary)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            color: 'white',
                            cursor: imageLoaded ? 'pointer' : 'not-allowed',
                            opacity: imageLoaded ? 1 : 0.5,
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 600
                        }}
                    >
                        <Check size={16} />
                        Apply Crop
                    </button>
                </div>
            </div>

            {/* Canvas area */}
            <div
                ref={containerRef}
                style={{ flex: 1, position: 'relative', overflow: 'hidden' }}
                onMouseDown={handleMouseDown}
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%'
                    }}
                />
                {!imageLoaded && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'rgba(255,255,255,0.5)'
                    }}>
                        Loading image...
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageCropCanvas;
